import { call, put, takeLatest } from "@redux-saga/core/effects";
import { activateUserRoles, AutoLoginRequest, change2FARequest, changePasswordRequest, changeWalletRequest, confirm2FARequest, resendVerifyEmailRequest, resetPasswordConfirmRequest, resetPasswordRequest, signinRequest, signupRequest, SignupResponseType, tfaForLoginRequest, verifyEmailRequest } from "../auth.api";
import * as AuthActions from './auth.actions';
import * as NftActions from '../../nfts/store/nfts.actions';
import { SigninActionType, SignupActionType, VerifyEmailActionType } from "./auth.action.types";
import { NavigateFunction } from "react-router-dom";
import axios from "axios";
import { ApiClient } from "../../../utils/apiClient";
import { Voucher } from "../../nfts/store/nfts.reducer";
import User from "../../../models/user.model";

const BASE_API = process.env.REACT_APP_BASE_API;
const apiClient = ApiClient(BASE_API);

function* signup(action: SignupActionType) {
    const { navigate, ...payload} = action.payload;
    const { data }: SignupResponseType = yield call(signupRequest, payload);
    if(data.status) {
        localStorage.setItem('emailVerifyToken', data.token);
        yield put(AuthActions.SignupSuccess({token: data.token, user: data.user}));
        navigate(`/auth/verify-email?email=${payload.email}`);
    } else {
        yield put(AuthActions.AuthenticationFailed(data.response));    
    }
}

function* verifyEmail(action: VerifyEmailActionType) {
    const { navigate, ...payload } = action.payload;

    const { data } = yield call(verifyEmailRequest, payload.token, payload.code);
    if(data.status) {
        yield put(AuthActions.AuthenticationSuccess({user: data.user, msg: data.msg}));
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.removeItem('emailVerifyToken');
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;
        navigate(`/auth/account-created?name=${data.user.name}`);
    } else {
        yield put(AuthActions.AuthenticationFailed(data.msg));
        axios.defaults.headers.common['Authorization'] = null;
    }
}

function* resendVerifyEmail(action: {type: string, payload: string}) {
    const { data } = yield call(resendVerifyEmailRequest, action.payload);
    if(data.status) {
        yield put(AuthActions.ResendSuccess(data.token));
    } else {
        yield put(AuthActions.AuthenticationFailed(data.msg));
    }
}

function* signIn(action: SigninActionType) {
    const {navigate, ...payload} = action.payload;
    const { data } = yield call(signinRequest, payload.email, payload.password);
    if(data.status) {

        // check 2fa or not!
        const user = data.user as User;
        if(user) {
            yield put(AuthActions.AuthenticationSuccess({user: data.user, msg: data.msg}));
            localStorage.setItem('accessToken', data.accessToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;

            // fetch vouchers
            const { data: vouchersResponse} = yield call(apiClient.listedVouchersRequest);
            if(vouchersResponse.status) {
                const rawVouchers = vouchersResponse.vouchers;
                const listed = rawVouchers.filter((voucher: Voucher) => voucher.listed);
                yield put(NftActions.GetVouchersSuccess(listed));
                
                const unlisted = rawVouchers.filter((voucher: Voucher) => 
                    (voucher.listed === false) && 
                    (voucher.owner === data.user.wallet)
                )
                const { data: nftsResponse } = yield call(apiClient.unlistedNftsRequest, 'data.user.wallet');
                yield put(NftActions.GetWalletTokensSuccess([...unlisted, ...nftsResponse.nfts]));
            } 

            
            navigate('/profile');
        } else {
            // go to 2fa page!
            yield put(AuthActions.Before2FALogin({
                method: data.method,
                token: data.token  
            }))
            navigate('/auth/login-tfa');
            axios.defaults.headers.common['Authorization'] = null;
        }
    } else {
        yield put(AuthActions.AuthenticationFailed(data.response));
        axios.defaults.headers.common['Authorization'] = null;
    }
}

function* changeWallet(action: {type: string, payload: string}) {
    const payload = action.payload;
    const { data } = yield call(changeWalletRequest, payload);
    if(data.status) {
        yield put(AuthActions.AuthenticationSuccess({user: data.user, msg: data.msg}));
    } else {
        yield put(AuthActions.AuthUpdateFailed(data.msg));
    }
}

function* changePassword(action: {type: string, payload: {old: string, newPassword: string}}) {
    const payload = action.payload;
    const { data } = yield call(changePasswordRequest, payload.old, payload.newPassword);
    if(data.status) {
        yield put(AuthActions.AuthUpdateSuccess('Password has been updated successfully.'))
    } else {
        yield put(AuthActions.AuthUpdateFailed(data.msg));
    }
}

function* tfaRequest(action: {type: string, payload: string}) {
    const { data } = yield call(change2FARequest, action.payload);
    if(data.status) {
        yield put(AuthActions.Change2FASuccess({
            msg: data.msg,
            token: data.token,
            email: data.email,
            qrcode: data.qrcode,
            loading: true
        }))
    } else {
        yield put(AuthActions.AuthUpdateFailed(data.msg));
    }
}

function* tfaConfirm(action: {type: string, payload: {
    token: string,
    code: string,
    method: string
}}) {
    const { token, code, method } = action.payload;
    const { data } = yield call(confirm2FARequest, token, code, method);
    if(data.status) {
        yield put(AuthActions.AuthenticationSuccess({user: data.user, msg: data.msg}));
        const { data: vouchersResponse} = yield call(apiClient.listedVouchersRequest);
        if(vouchersResponse.status) {
            const rawVouchers = vouchersResponse.vouchers;
            const listed = rawVouchers.filter((voucher: Voucher) => voucher.listed);
            yield put(NftActions.GetVouchersSuccess(listed));
            
            const unlisted = rawVouchers.filter((voucher: Voucher) => 
            (voucher.listed === false) && 
            (voucher.owner === data.user.wallet)
            )
            console.log('unlisted vouchers: ', unlisted);

            const { data: nftsResponse } = yield call(apiClient.unlistedNftsRequest, "data.user.wallet");
            yield put(NftActions.GetWalletTokensSuccess([...unlisted, ...nftsResponse.nfts]));
        } 
    } else {
        yield put(AuthActions.AuthUpdateFailed(data.msg));
    }
}

function* tfaForLogin(action: {type: string, payload: {
    token: string,
    code: string,
    navigate: NavigateFunction
}}) {
    const { navigate, ...payload } = action.payload;
    const { data } = yield call(tfaForLoginRequest, payload.token, payload.code);
    if(data.status) {
        yield put(AuthActions.AuthenticationSuccess({user: data.user, msg: data.msg}));
        localStorage.setItem('accessToken', data.accessToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;

        // fetch vouchers
        const { data: vouchersResponse} = yield call(apiClient.listedVouchersRequest);
        if(vouchersResponse.status) {
            const rawVouchers = vouchersResponse.vouchers;
            const listed = rawVouchers.filter((voucher: Voucher) => voucher.listed);
            yield put(NftActions.GetVouchersSuccess(listed));
            
            const unlisted = rawVouchers.filter((voucher: Voucher) => 
            (voucher.listed === false) && 
            (voucher.owner === data.user.wallet)
            )
            console.log('unlisted vouchers: ', unlisted);

            const { data: nftsResponse } = yield call(apiClient.unlistedNftsRequest, "data.user.wallet");
            yield put(NftActions.GetWalletTokensSuccess([...unlisted, ...nftsResponse.nfts]));
        } 
        navigate('/profile');
    } else {
        yield put(AuthActions.TfaForLoginFailed(data.msg));
        axios.defaults.headers.common['Authorization'] = null;
    }
}

function* AutoLogin(action: {type: string, payload: {
    accessToken: string, 
    path: string,
    navigate: NavigateFunction}}
) {
    try {
        const { navigate, ...payload } = action.payload;
        const { data } = yield call(AutoLoginRequest, payload.accessToken);
        if (data.status) {
            yield put(AuthActions.AuthenticationSuccess({
                user: data.user,
                msg: data.msg,
                token: payload.accessToken,
            }));
            axios.defaults.headers.common['Authorization'] = `Bearer ${payload.accessToken}`;
            navigate(payload.path);
        } else {
            
            yield put(AuthActions.AuthenticationFailed(data.msg));
            delete axios.defaults.headers.common['Authorization'];
            localStorage.removeItem('accessToken');
            localStorage.removeItem("skip-dialog");
            navigate('/auth/login');
        }
    } catch (error) {
        console.error("Eroare în AutoLogin:", error);
        yield put(AuthActions.AuthenticationFailed("A apărut o eroare neașteptată."));
        delete axios.defaults.headers.common['Authorization'];
        localStorage.removeItem('accessToken');
        localStorage.removeItem("skip-dialog");
        action.payload.navigate('/auth/login');
    }
}


function* logout(action: {type: string, payload: NavigateFunction}) {
    // clear localStorage
    localStorage.removeItem('accessToken');
    localStorage.removeItem("skip-dialog");

    // navigate into login path
    action.payload('/auth/login');

    yield put(AuthActions.LogoutSuccess());
}

function* requestResetPassword(action: {type: string, payload: {navigate: NavigateFunction, email: string}}) {
    const {navigate, email} = action.payload;
    const { data } = yield call(resetPasswordRequest, email);
    if(data.status) {
        yield put(AuthActions.ResetPasswordSuccess({msg: data.msg, token: data.token}))
        // redirect to login
        navigate(`/auth/confirm-reset-password?email=${email}`);
    } else {
        yield put(AuthActions.AuthUpdateFailed(data.response));
    }
}

function* confirmResetPassword(action: {type: string, payload: {
    navigate: NavigateFunction, 
    newPassword: string, 
    token: string,
    code: string
}}) {
    const { navigate, ...payload} = action.payload;
    const { data } = yield call(resetPasswordConfirmRequest, payload.token, payload.code, payload.newPassword);
    if(data.status) {
        yield put(AuthActions.AuthUpdateSuccess(data.msg));
        navigate('/auth/reset-password-success');
    } else {
        yield put(AuthActions.AuthUpdateFailed(data.msg));
    }
}

function* changeRole(action: {type: string, payload: {role: string, activate: number}}) {
    const { role, activate } = action.payload;
    const { data } = yield call(activateUserRoles, role, activate);
    if(data.status) {
        localStorage.setItem("accessToken", data.accessToken);
        yield put(AuthActions.ChangeRoleSuccess({user: data.user, msg: data.msg}));
    } else {
        yield put(AuthActions.ChangeRoleFailed(data.msg || 'Failed to update role'));
    }
}


function* watchAuthActions () {
    yield takeLatest(AuthActions.SIGNUP_START, signup);
    yield takeLatest(AuthActions.VERIFY_EMAIL, verifyEmail);
    yield takeLatest(AuthActions.RESEND_VERIFY_EMAIL, resendVerifyEmail);
    yield takeLatest(AuthActions.SIGNIN_START, signIn);
    yield takeLatest(AuthActions.CHANGE_WALLET, changeWallet);
    yield takeLatest(AuthActions.CHANGE_PASSWORD, changePassword);
    yield takeLatest(AuthActions.CHANGE_2FA, tfaRequest);
    yield takeLatest(AuthActions.CONFIRM_2FA, tfaConfirm);
    yield takeLatest(AuthActions.TFA_FOR_LOGIN, tfaForLogin);
    yield takeLatest(AuthActions.AUTO_LOGIN, AutoLogin);
    yield takeLatest(AuthActions.LOGOUT, logout);
    yield takeLatest(AuthActions.RESET_PASSWORD, requestResetPassword);
    yield takeLatest(AuthActions.CONFIRM_RESET_PASSWORD, confirmResetPassword);
    yield takeLatest(AuthActions.CHANGE_ROLE, changeRole);
}

export default watchAuthActions;